import React, { useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { FieldInput } from "@/components/CrudPage";
import { fmtNum } from "@/components/KpiCard";
import { netMinutes } from "@/lib/shift";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const today = () => new Date().toISOString().slice(0, 10);

const BASE_NUM_FIELDS = [
  { name: "plan", label: "Plan" },
  { name: "actual", label: "Gerçek Üretim", required: true },
  { name: "good", label: "İyi Ürün" },
  { name: "scrap", label: "Hatalı / Fire" },
  { name: "work_minutes", label: "Çalışma Süresi (dk)" },
  { name: "overtime_minutes", label: "Fazla Mesai (dk)" },
  { name: "downtime_minutes", label: "Duruş (dk)" },
];

const MACHINE_NUM_FIELDS = [
  { name: "setup_minutes", label: "Setup (dk)" },
  { name: "breakdown_minutes", label: "Arıza (dk)" },
];

const MESAI_OPTS = ["Normal Mesai", "Ek Mesai", "Extra Mesai", "Cumartesi", "Pazar"]
  .map((v) => ({ value: v, label: v }));

const INITIAL_FORM = {
  department_code: "", machine_code: "", product_code: "", operation_code: "",
  employee_code: "", team_name: "", plan: "", actual: "", good: "", scrap: "",
  work_minutes: 480, overtime_minutes: 0, downtime_minutes: 0, setup_minutes: 0,
  breakdown_minutes: 0, shift: "", mesai_type: "Normal Mesai", note: "",
};

function useEntryForm(type, meta, onSaved) {
  const [form, setForm] = useState(() => ({ ...INITIAL_FORM, date: today() }));
  const [saving, setSaving] = useState(false);
  const set = (n, v) => setForm((f) => {
    const nf = { ...f, [n]: v };
    if (n === "machine_code") {
      const m = meta.machines.find((x) => x.code === v);
      if (m) nf.department_code = m.department_code;
      nf.product_code = "";
    }
    if (n === "shift") {
      const s = (meta.shift_definitions || []).find((x) => x.code === v);
      if (s) nf.work_minutes = netMinutes(s);
    }
    if (n === "actual" && !f.good) nf.good = v;
    return nf;
  });
  const machine = meta.machines.find((m) => m.code === form.machine_code);
  const selCycle = machine?.product_cycles?.find((pc) => pc.product_code === form.product_code);
  const save = async () => {
    if (!form.date || !form.department_code || form.actual === "") {
      toast.error("Tarih, bölüm ve gerçek üretim zorunludur"); return;
    }
    if (form.product_code && !form.operation_code) {
      toast.error("Standart kapasite hesabı için operasyon seçin"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, entry_type: type, cycle_time_sec: selCycle?.cycle_time_sec };
      for (const k of ["machine_code", "employee_code", "team_name", "product_code", "operation_code", "note"])
        if (!payload[k]) delete payload[k];
      if (type === "manual") delete payload.machine_code;
      if (type === "team") delete payload.employee_code;
      const r = await api.post("/entries", payload);
      toast.success(`Kayıt eklendi — Standart kapasite: ${fmtNum(r.data.standard_capacity)}, Verimlilik: ${r.data.efficiency != null ? `%${r.data.efficiency}` : "-"}`);
      setForm((f) => ({ ...f, plan: "", actual: "", good: "", scrap: "", note: "" }));
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  return { form, set, save, saving, machine, selCycle };
}

export function EntryForm({ type, meta, onSaved }) {
  const isMachine = type === "machine";
  const { form, set, save, saving, machine, selCycle } = useEntryForm(type, meta, onSaved);
  const machineProducts = isMachine && machine?.product_cycles?.length
    ? machine.product_cycles.map((pc) => {
        const p = meta.products.find((x) => x.code === pc.product_code);
        return { value: pc.product_code, label: `${pc.product_code} — ${p?.name || ""} (${pc.cycle_time_sec}sn${pc.cavities ? `, ${pc.cavities} göz` : ""})` };
      })
    : meta.products.map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` }));

  const deptOps = meta.operations.filter((o) => !form.department_code || o.department_code === form.department_code);

  const numFields = isMachine ? [...BASE_NUM_FIELDS, ...MACHINE_NUM_FIELDS] : BASE_NUM_FIELDS;

  return (
    <div className="space-y-4" data-testid={`entry-form-${type}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Tarih *</Label>
          <FieldInput field={{ type: "date", name: "date" }} value={form.date} onChange={set} testid={`entry-${type}-date`} />
        </div>
        {isMachine ? (
          <div className="space-y-1.5">
            <Label className="text-xs">Makine *</Label>
            <FieldInput field={{ name: "machine_code", type: "select", options: meta.machines.map((m) => ({ value: m.code, label: `${m.code} — ${m.name}` })) }}
              value={form.machine_code} onChange={set} testid={`entry-${type}-machine`} />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-xs">Bölüm *</Label>
            <FieldInput field={{ name: "department_code", type: "select", options: meta.departments.map((d) => ({ value: d.code, label: d.name })) }}
              value={form.department_code} onChange={set} testid={`entry-${type}-department`} />
          </div>
        )}
        {type === "manual" && (
          <div className="space-y-1.5">
            <Label className="text-xs">Personel</Label>
            <FieldInput field={{ name: "employee_code", type: "select", options: meta.employees.filter((e) => !form.department_code || (e.department_codes || []).includes(form.department_code)).map((e) => ({ value: e.code, label: e.name })) }}
              value={form.employee_code} onChange={set} testid={`entry-${type}-employee`} />
          </div>
        )}
        {type === "team" && (
          <div className="space-y-1.5">
            <Label className="text-xs">Ekip</Label>
            <FieldInput field={{ name: "team_name", type: "select", options: meta.teams.map((t) => ({ value: t.name, label: t.name })) }}
              value={form.team_name} onChange={set} testid={`entry-${type}-team`} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">Ürün {selCycle ? `(çevrim: ${selCycle.cycle_time_sec}sn${selCycle.cavities ? ` × ${selCycle.cavities} göz` : ""})` : ""}</Label>
          <FieldInput field={{ name: "product_code", type: "select", options: machineProducts }}
            value={form.product_code} onChange={set} testid={`entry-${type}-product`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Operasyon</Label>
          <FieldInput field={{ name: "operation_code", type: "select", options: deptOps.map((o) => ({ value: o.code, label: o.name })) }}
            value={form.operation_code} onChange={set} testid={`entry-${type}-operation`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Vardiya / Mesai Düzeni</Label>
          <FieldInput field={{ name: "shift", type: "select", options: (meta.shift_definitions || []).map((s) => ({ value: s.code, label: `${s.name} — net ${netMinutes(s)} dk` })) }}
            value={form.shift} onChange={set} testid={`entry-${type}-shift`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Mesai Tipi</Label>
          <FieldInput field={{ name: "mesai_type", type: "select", options: MESAI_OPTS }}
            value={form.mesai_type} onChange={set} testid={`entry-${type}-mesai-type`} />
        </div>
        {numFields.map((f) => (
          <div key={f.name} className="space-y-1.5">
            <Label className="text-xs">{f.label}{f.required && " *"}</Label>
            <FieldInput field={{ ...f, type: "number" }} value={form[f.name]} onChange={set} testid={`entry-${type}-${f.name}`} />
          </div>
        ))}
        <div className="space-y-1.5 col-span-2">
          <Label className="text-xs">Açıklama</Label>
          <FieldInput field={{ name: "note" }} value={form.note} onChange={set} testid={`entry-${type}-note`} />
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid={`entry-${type}-save`}>
        {saving ? "Kaydediliyor..." : "Üretim Kaydını Kaydet"}
      </Button>
      <p className="text-xs text-muted-foreground">
        Standart kapasite; tanımlı standart süre, net çalışma süresi (çalışma − duruş − setup) ve göz sayısı üzerinden otomatik hesaplanır.
      </p>
    </div>
  );
}
